import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { ArrowUpRight, ChevronRight } from "lucide-react";

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 22 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const valueProps = [
  {
    title: "Disciplined underwriting",
    desc: "Structured evaluation grounded in defined criteria and long-term alignment.",
  },
  {
    title: "Concentrated portfolio",
    desc: "Selective capital deployment with meaningful engagement.",
  },
  {
    title: "Regulated market focus",
    desc: "Investment in sectors where structural complexity supports defensibility.",
  },
];

const focusAreas = [
  {
    title: "Financial Infrastructure & Payments",
    desc: "Capital flows, treasury, and compliance-driven financial systems.",
  },
  {
    title: "Vertical & Enterprise Software",
    desc: "Mission-critical platforms in regulated industries.",
  },
  {
    title: "Risk & Operational Systems",
    desc: "Governance, compliance, and capital management infrastructure.",
  },
];

const profileLeft = [
  { label: "Stage", value: "Seed and Series A" },
  { label: "Geography", value: "Canada, with selective US and cross-border expansion" },
  { label: "Initial investment", value: "CAD $250K–$1M" },
];

const profileRight = [
  { label: "Ownership target", value: "5–10%" },
  { label: "Investment role", value: "Lead and co-lead, with selective follow-ons" },
  { label: "Portfolio model", value: <>Concentrated, high-conviction portfolio<br className="hidden md:inline" />{" "}(10–20 core investments)</> },
];

const processSteps = [
  {
    num: "01",
    title: "Initial Review",
    desc: <>Assessment of submitted materials for alignment with stage, sector focus,<br className="hidden md:inline" />{" "}and investment criteria.</>,
  },
  {
    num: "02",
    title: "Introductory Discussion",
    desc: <>A focused conversation to understand product positioning, traction,<br className="hidden md:inline" />{" "}and capital objectives.</>,
  },
  {
    num: "03",
    title: "Partner Evaluation",
    desc: <>In-depth review of distribution model, economics, governance readiness,<br className="hidden md:inline" />{" "}and long-term positioning.</>,
  },
  {
    num: "04",
    title: "Diligence",
    desc: "Structured analysis of customers, metrics, team composition, and capital framework.",
  },
  {
    num: "05",
    title: "Decision & Next Steps",
    desc: "Clear feedback and defined next steps following internal review.",
  },
];

const prioritize = [
  "A clearly defined customer and problem",
  "Early evidence of demand (revenue, pilots, renewals, or meaningful usage)",
  "A focused and repeatable go-to-market model",
  "Disciplined capital planning and clear use of proceeds",
  "Governance readiness and financial clarity",
];

const additionalStrength = [
  "Clear economics and a realistic path to scale",
  "Customer retention and proof beyond pipeline",
  "A defined milestone plan over the next 6\u201318 months",
  "Strong execution discipline within the founding team",
];



function TypewriterStatement({ text }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [displayed, setDisplayed] = useState("");

  useEffect(() => {
    if (!inView) return;
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) clearInterval(interval);
    }, 22);
    return () => clearInterval(interval);
  }, [inView, text]);

  return (
    <p
      ref={ref}
      className="font-body text-[17px] md:text-[19px] leading-[1.7] text-[#1e293b] font-medium max-w-3xl mx-auto"
      data-testid="coinvest-typewriter"
    >
      {displayed}
      {inView && displayed.length < text.length && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.5, repeat: Infinity }}
          className="inline-block w-[2px] h-[1.1em] bg-[#4F6BFF] ml-0.5 align-middle"
        />
      )}
    </p>
  );
}


function HowWeWork({ steps }) {
  const hwwRef = useRef(null);
  const hwwInView = useInView(hwwRef, { once: true, margin: "-80px" });

  return (
    <section className="py-[30px] md:py-[50px]" data-testid="how-we-work-section" ref={hwwRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="structured-bg wave-lines relative rounded-[2rem] overflow-hidden p-8 md:p-12 lg:p-14">
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-center">
            {/* Left — title + description, vertically centered */}
            <div className="lg:col-span-5 flex items-center">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={hwwInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <div>
                  <h2 className="font-body text-[30px] sm:text-[45px] font-semibold text-white tracking-tight" style={{ marginBottom: '19px' }}>
                    How we work
                  </h2>
                  <p className="font-body text-[17px] leading-[1.75] text-white max-w-sm">
                    We follow a structured and focused process designed to maintain underwriting discipline while moving efficiently. Each stage is intended to ensure clarity, alignment, and defined expectations.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right — timeline steps */}
            <div className="lg:col-span-7" data-testid="process-timeline">
              <div className="relative pl-0">
                {/* Vertical line — through center of markers */}
                <div className="absolute top-2 bottom-2 w-px bg-white/[0.15]" style={{ left: '7px' }} />

                <div className="space-y-4">
                  {steps.map((step, i) => (
                    <motion.div
                      key={step.num}
                      initial={{ opacity: 0, x: 30 }}
                      animate={hwwInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.5, delay: 0.3 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <div
                        className="relative flex items-start gap-5 py-1"
                        data-testid={`process-step-${i}`}
                      >
                        {/* Circle marker — vertically centered with title */}
                        <div className="relative z-10 flex-shrink-0 w-[14px] h-[14px] rounded-full bg-white mt-[5px]" />
                        <div className="flex-1">
                          <h3 className="font-body text-[18px] font-semibold text-white tracking-tight mb-0.5">
                            {step.title}
                          </h3>
                          <p className="font-body text-[15px] leading-[1.6] text-white/80">
                            {step.desc}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


export default function InvestmentStrategy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, margin: "-40px" });
  const ctaRef = useRef(null);
  const ctaInView = useInView(ctaRef, { once: true, margin: "-100px" });
  const vpRef = useRef(null);
  const vpInView = useInView(vpRef, { once: true, margin: "-80px" });
  const profileRef = useRef(null);
  const profileInView = useInView(profileRef, { once: true, margin: "-80px" });
  const lookForRef = useRef(null);
  const lookForInView = useInView(lookForRef, { once: true, margin: "-80px" });

  return (
    <div className="min-h-screen bg-white" data-testid="investment-strategy-page">
      <Navbar />

      {/* ═══════ EYEBROW + H1 TITLE ═══════ */}
      <section
        className="relative overflow-hidden pt-32 md:pt-40 pb-6 md:pb-[47px]"
        style={{ background: "linear-gradient(180deg, #f8faff 0%, #f3f5fb 50%, #f8faff 100%)" }}
      >
        <div className="absolute inset-0 grid-pattern opacity-[0.06]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="flex items-center justify-center gap-3 mb-8"
          >
            <div className="w-8 h-px bg-[#4F6BFF]" />
            <span className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[#4F6BFF]/80">Investment Strategy</span>
            <div className="w-8 h-px bg-[#4F6BFF]" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-body text-[30px] sm:text-[45px] tracking-tight leading-tight font-semibold text-[#08122a]"
            data-testid="strategy-page-title"
          >
            A disciplined early-stage strategy<br className="hidden md:inline" />{" "}in regulated financial markets
          </motion.h1>
        </div>
      </section>

      {/* ═══════ HERO BLOCK — 2 COLUMNS ═══════ */}
      <section
        ref={heroRef}
        className="relative overflow-hidden pb-6 md:pb-8"
        data-testid="strategy-hero"
        style={{ background: "linear-gradient(180deg, #f8faff 0%, #ffffff 100%)" }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Left — hero statement + paragraph */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-7"
              data-testid="strategy-heading"
            >
              <p className="font-body text-[15px] md:text-base leading-[1.75] text-[#475569] max-w-4xl mb-5">
                Our approach is thesis-led and focused on regulated and infrastructure-led markets. We invest where product validation is emerging and distribution models are being defined, supporting the shift from early traction to repeatable, scalable execution.
              </p>
              <p className="font-body text-[15px] md:text-base leading-[1.75] text-[#475569] max-w-4xl">
                Capital is deployed selectively, with defined ownership targets and a long-term perspective. Engagement strengthens governance, distribution discipline, and capital allocation clarity, while follow-on participation reflects sustained performance and alignment.
              </p>
            </motion.div>

            {/* Right — abstract geometric visual (higher contrast) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={heroInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="relative flex items-center justify-center lg:col-span-5"
            >
              <div className="relative w-full max-w-[240px] aspect-square mx-auto mt-[10px]">
                {/* Radial glow */}
                <div className="absolute inset-0 rounded-full blur-[50px]"
                  style={{ background: "radial-gradient(circle, rgba(79,107,255,0.18) 0%, rgba(123,97,255,0.10) 40%, transparent 70%)" }}
                />
                {/* Orbital rings */}
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 220 220" fill="none">
                  <circle cx="110" cy="110" r="104" stroke="rgba(79,107,255,0.18)" strokeWidth="1.2" />
                  <circle cx="110" cy="110" r="78" stroke="rgba(79,107,255,0.13)" strokeWidth="1" />
                  <circle cx="110" cy="110" r="50" stroke="rgba(123,97,255,0.11)" strokeWidth="1" />
                  <circle cx="110" cy="110" r="24" stroke="rgba(79,107,255,0.14)" strokeWidth="1" />
                  {/* Axis lines */}
                  <line x1="110" y1="6" x2="110" y2="214" stroke="rgba(79,107,255,0.07)" strokeWidth="1" />
                  <line x1="6" y1="110" x2="214" y2="110" stroke="rgba(79,107,255,0.07)" strokeWidth="1" />
                  {/* Diagonal axes */}
                  <line x1="36" y1="36" x2="184" y2="184" stroke="rgba(79,107,255,0.05)" strokeWidth="1" />
                  <line x1="184" y1="36" x2="36" y2="184" stroke="rgba(79,107,255,0.05)" strokeWidth="1" />
                  {/* Static accent dots */}
                  <circle cx="110" cy="6" r="2" fill="rgba(79,107,255,0.20)" />
                  <circle cx="214" cy="110" r="1.5" fill="rgba(123,97,255,0.18)" />
                  <circle cx="110" cy="214" r="1.5" fill="rgba(79,107,255,0.15)" />
                  <circle cx="6" cy="110" r="1.5" fill="rgba(123,97,255,0.16)" />
                  <circle cx="166" cy="32" r="1.5" fill="rgba(79,107,255,0.14)" />
                  <circle cx="54" cy="188" r="1.5" fill="rgba(79,107,255,0.12)" />
                </svg>
                {/* Orbiting dot 1 */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <motion.div
                    className="relative w-[208px] h-[208px]"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
                  >
                    <div className="absolute w-[6px] h-[6px] rounded-full"
                      style={{ top: 0, left: "calc(50% - 3px)", background: "#4F6BFF", boxShadow: "0 0 12px rgba(79,107,255,0.7), 0 0 3px rgba(79,107,255,0.9)" }}
                    />
                  </motion.div>
                </div>
                {/* Orbiting dot 2 */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <motion.div
                    className="relative w-[156px] h-[156px]"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
                  >
                    <div className="absolute w-[4px] h-[4px] rounded-full"
                      style={{ bottom: 0, left: "calc(50% - 2px)", background: "#7B61FF", boxShadow: "0 0 8px rgba(123,97,255,0.6), 0 0 3px rgba(123,97,255,0.8)" }}
                    />
                  </motion.div>
                </div>
                {/* Orbiting dot 3 */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <motion.div
                    className="relative w-[100px] h-[100px]"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 16, repeat: Infinity, ease: "linear" }}
                  >
                    <div className="absolute w-[3px] h-[3px] rounded-full"
                      style={{ top: 0, left: "calc(50% - 1.5px)", background: "#4F6BFF", boxShadow: "0 0 6px rgba(79,107,255,0.5)" }}
                    />
                  </motion.div>
                </div>
                {/* Center node */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="w-3.5 h-3.5 rounded-full bg-[#4F6BFF]/20 flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#4F6BFF]/40" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ 2. VALUE PROPOSITION ═══════ */}
      <section className="py-[30px] md:py-[50px]" data-testid="value-proposition-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-10">
              <h2 className="font-body text-[30px] sm:text-[45px] font-semibold text-[#08122a] tracking-tight">
                Our value proposition
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="value-props-grid">
            {valueProps.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.07}>
                <div
                  className="rounded-2xl border border-black/[0.04] p-8 bg-white card-gradient-line portfolio-card h-full"
                  data-testid={`value-prop-${i}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#4F6BFF]/10 to-[#7B61FF]/10 flex items-center justify-center mb-6">
                    <div className="w-2 h-2 rounded-full bg-[#4F6BFF]" />
                  </div>
                  <h3 className="font-body text-[18px] font-semibold text-[#08122a] tracking-tight mb-3">
                    {item.title}
                  </h3>
                  <p className="font-body text-[15px] leading-[1.7] text-[#475569]">
                    {item.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 3. AREAS OF FOCUS ═══════ */}
      <section className="py-[30px] md:py-[50px]" data-testid="areas-of-focus-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="structured-bg wave-lines relative rounded-[2rem] overflow-hidden p-8 md:p-12 lg:p-14">
              <div className="relative z-10">
                <div className="mb-10">
                  <h2 className="font-body text-[30px] sm:text-[45px] font-semibold text-white tracking-tight mb-3">
                    Areas of focus
                  </h2>
                  <p className="font-body text-lg text-white/70">
                    Where capital meets infrastructure.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-testid="focus-areas-grid">
                  {focusAreas.map((area, i) => (
                    <div
                      key={area.title}
                      className="rounded-xl p-6 border border-white/10"
                      style={{ background: "rgba(255,255,255,0.06)", backdropFilter: "blur(8px)" }}
                      data-testid={`focus-area-${i}`}
                    >
                      <h3 className="font-body text-[16px] font-semibold text-white tracking-tight mb-2">
                        {area.title}
                      </h3>
                      <p className="font-body text-[14px] leading-[1.7] text-white/65">
                        {area.desc}
                      </p>
                    </div>
                  ))}
                </div>

                <p className="relative font-body text-[14px] text-white/50 leading-relaxed">
                  Across these areas, we prioritize businesses with clear commercial pathways, defensible positioning, and sustained execution discipline.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ 4. INVESTMENT PROFILE ═══════ */}
      <section className="py-[30px] md:py-[50px]" data-testid="investment-profile-section" ref={profileRef}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="font-body text-[30px] sm:text-[45px] font-semibold text-[#08122a] tracking-tight">
              Investment profile
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl mx-auto" data-testid="profile-grid">
            {/* Left panel */}
            <div
              className="card-gradient-line rounded-2xl border border-black/[0.04] bg-white p-7 md:p-8 h-full"
              style={{ boxShadow: '0 10px 30px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)' }}
            >
              <div className="space-y-0">
                {profileLeft.map((item, i) => (
                  <div
                    key={item.label}
                    className={`py-5 ${i < profileLeft.length - 1 ? "border-b border-black/[0.05]" : ""}`}
                    data-testid={`profile-item-${i}`}
                  >
                    <span className="font-body text-[11px] font-semibold uppercase tracking-[0.16em] text-[#94a3b8] block mb-1.5">
                      {item.label}
                    </span>
                    <span className="font-body text-[17px] font-semibold text-[#08122a] leading-snug">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right panel */}
            <div
              className="card-gradient-line rounded-2xl border border-black/[0.04] bg-white p-7 md:p-8 h-full"
              style={{ boxShadow: '0 10px 30px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)' }}
            >
              <div className="space-y-0">
                {profileRight.map((item, i) => (
                  <div
                    key={item.label}
                    className={`py-5 ${i < profileRight.length - 1 ? "border-b border-black/[0.05]" : ""}`}
                    data-testid={`profile-item-${i + 3}`}
                  >
                    <span className="font-body text-[11px] font-semibold uppercase tracking-[0.16em] text-[#94a3b8] block mb-1.5">
                      {item.label}
                    </span>
                    <span className="font-body text-[17px] font-semibold text-[#08122a] leading-snug">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ 5. HOW WE WORK ═══════ */}
      <HowWeWork steps={processSteps} />

      {/* ═══════ 6. WHAT WE LOOK FOR ═══════ */}
      <section className="py-[30px] md:py-[50px]" data-testid="what-we-look-for-section" ref={lookForRef}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={lookForInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-5"
          >
            <h2 className="font-body text-[30px] sm:text-[45px] font-semibold text-[#08122a] tracking-tight mb-3">
              What we look for
            </h2>
            <p className="font-body text-[15px] md:text-base leading-[1.75] text-[#475569] max-w-2xl mx-auto">
              We are a strong fit for teams that demonstrate the fundamentals required to build a durable company and progress toward the next stage{' '}
              <br className="md:hidden" />
              of institutional capital.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10" data-testid="look-for-panels">
            <motion.div
              initial={{ opacity: 0, y: 60, scale: 0.95 }}
              animate={lookForInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6, boxShadow: "0 20px 40px rgba(15,23,42,0.12), 0 4px 12px rgba(15,23,42,0.06)", transition: { duration: 0.15 } }}
              className="card-gradient-line rounded-2xl border border-black/[0.04] bg-white p-7 md:p-8 h-full cursor-default"
              style={{ boxShadow: '0 10px 30px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)' }}
              data-testid="prioritize-panel"
            >
              <h3 className="font-body text-[18px] font-semibold text-[#08122a] tracking-tight mb-6">
                We prioritize
              </h3>
              <ul className="space-y-4">
                {prioritize.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-[6px] h-[6px] rounded-full bg-[#4F6BFF]/50 flex-shrink-0 mt-[9px]" />
                    <span className="font-body text-[15px] leading-[1.7] text-[#334155]">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 60, scale: 0.95 }}
              animate={lookForInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6, boxShadow: "0 20px 40px rgba(15,23,42,0.12), 0 4px 12px rgba(15,23,42,0.06)", transition: { duration: 0.15 } }}
              className="card-gradient-line rounded-2xl border border-black/[0.04] bg-white p-7 md:p-8 h-full cursor-default"
              style={{ boxShadow: '0 10px 30px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)' }}
              data-testid="strength-panel"
            >
              <h3 className="font-body text-[18px] font-semibold text-[#08122a] tracking-tight mb-6">
                Additional strength is reflected in
              </h3>
              <ul className="space-y-4">
                {additionalStrength.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-[6px] h-[6px] rounded-full bg-[#4F6BFF]/50 flex-shrink-0 mt-[9px]" />
                    <span className="font-body text-[15px] leading-[1.7] text-[#334155]">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ CO-INVESTMENT STATEMENT ═══════ */}
      <section className="py-[30px] md:py-[50px]" data-testid="coinvest-statement">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <TypewriterStatement text="We welcome co-investment discussions with aligned venture funds and institutional partners on select transactions." />
        </div>
      </section>

      {/* ═══════ 7. CLOSING CTA ═══════ */}
      <section className="pt-[30px] pb-[60px] md:py-[50px]" data-testid="strategy-cta-section" ref={ctaRef}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            animate={ctaInView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[2rem] overflow-hidden py-12 md:py-16 px-8 md:px-16 text-center border border-black/[0.04]"
            style={{ background: "linear-gradient(180deg, #f8faff 0%, #f0f4ff 50%, #f5f7fc 100%)", boxShadow: "0 10px 30px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)" }}
            data-testid="strategy-cta-block"
          >
            {/* Subtle grid */}
            <div className="absolute inset-0 grid-pattern opacity-10" />

            {/* Vignette */}
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: "radial-gradient(ellipse at center, transparent 30%, rgba(15,23,42,0.025) 100%)" }}
            />

            {/* Left circle */}
            <div className="absolute pointer-events-none top-[15%] left-[-30%] md:left-[-12%] hidden md:block">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[420px] md:h-[420px] rounded-full blur-[90px]"
                style={{ background: "radial-gradient(circle, rgba(79,107,255,0.12) 0%, transparent 65%)" }}
              />
              <svg width="480" height="480" viewBox="0 0 480 480" fill="none" className="w-[300px] h-[300px] md:w-[480px] md:h-[480px]">
                <circle cx="240" cy="240" r="230" stroke="rgba(79,107,255,0.16)" strokeWidth="1" />
                <circle cx="240" cy="240" r="165" stroke="rgba(79,107,255,0.11)" strokeWidth="1" />
                <circle cx="240" cy="240" r="90" stroke="rgba(79,107,255,0.07)" strokeWidth="1" />
              </svg>
            </div>

            {/* Right circle with animation and orbiting dot */}
            <motion.div
              className="absolute pointer-events-none"
              style={{ bottom: "2%", right: "-8%" }}
              animate={{ x: [0, -12, 5, 0], y: [0, -10, 8, 0], scale: [1, 1.05, 0.97, 1] }}
              transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[220px] h-[220px] md:w-[310px] md:h-[310px] rounded-full blur-[80px]"
                style={{ background: "radial-gradient(circle, rgba(123,97,255,0.18) 0%, transparent 65%)" }}
              />
              <svg width="360" height="360" viewBox="0 0 360 360" fill="none" className="w-[225px] h-[225px] md:w-[360px] md:h-[360px]">
                <circle cx="180" cy="180" r="172" stroke="rgba(123,97,255,0.22)" strokeWidth="1.2" />
                <circle cx="180" cy="180" r="120" stroke="rgba(123,97,255,0.16)" strokeWidth="1" />
                <circle cx="180" cy="180" r="60" stroke="rgba(123,97,255,0.10)" strokeWidth="1" />
                <circle r="4" fill="rgba(123,97,255,0.55)">
                  <animateMotion dur="18s" repeatCount="indefinite" path="M 180 8 A 172 172 0 0 1 352 180 A 172 172 0 0 1 180 352 A 172 172 0 0 1 8 180 A 172 172 0 0 1 180 8" />
                  <animate attributeName="opacity" values="0.55;0.25;0.55" dur="3s" repeatCount="indefinite" />
                </circle>
              </svg>
            </motion.div>

            {/* Central glow */}
            <div className="absolute bottom-[14%] left-1/2 -translate-x-1/2 pointer-events-none">
              <div className="w-[400px] h-[400px] md:w-[550px] md:h-[550px] rounded-full blur-[120px]"
                style={{ background: "radial-gradient(circle at center, rgba(79,107,255,0.22) 0%, rgba(123,97,255,0.12) 35%, transparent 70%)" }}
              />
            </div>

            <div className="relative z-10">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                animate={ctaInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="font-body text-[30px] sm:text-[45px] tracking-tight leading-tight font-semibold text-[#08122a] mb-[21px]"
              >
                Connect
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 25 }}
                animate={ctaInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="font-body text-base md:text-lg leading-relaxed text-[#475569] max-w-xl mx-auto mb-8"
              >
                If your company aligns with our strategy, we welcome your inquiry.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={ctaInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
              >
                <a
                  href="mailto:inquiries@nebula-f.com"
                  data-testid="strategy-cta-button"
                  className="connect-cta-btn group inline-flex items-center gap-2 px-[18px] py-[10px] text-white text-[14px] font-semibold rounded-full transition-all duration-300 hover:-translate-y-0.5"
                >
                  Start a conversation
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
